import ImageBlock from '@component/common/block/image-block';
import type { IAnnotationPhotoData } from '@datatype/gridgio/api-type';
import Handwriting from '@design-system/handwriting';
import React from 'react';
import { v4 as uuid4 } from 'uuid';
import styled from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import Line from '@component/common/line';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import TextButton from '@design-system/text-button';
import Chip from '@design-system/chip';
import * as color from '@constant/color';
import * as size from '@constant/size';
import type { IAnnotation, IAnnotationMemo } from '@datatype/gridgio/type';
import { ETheme } from '@datatype/type';

interface IProps {
  issueName: string;
  memo: IAnnotationMemo[];
  photoCount: number;
  photoMessage?: string;
  photos?: IAnnotationPhotoData[];
  photoBusy?: boolean;
  photoRetryable?: boolean;
  handlePhotoRetryClick?: () => void;
  handlePhotoCancelClick?: () => void;
  handlePhotoDeleteClick?: (key: string) => void;
  handlePhotoOpenClick?: (url: string) => void;
  property?: IAnnotation['property'];
  handlePropertyClick?: () => void;
  handleMemoAddClick?: () => void;
  handleMemoListClick?: () => void;
  handleMemoClick?: (memo: IAnnotationMemo) => void;
  handlePhotoClick?: () => void;
  handleDeleteClick?: () => void;
}

const PhotoItemsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: ${size.MARGIN_1};
`;
const PhotoItemContainer = styled.div`
  display: flex;
  width: calc(50% - 5px);
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
`;
const PhotoControlContainer = styled.div`
  display: flex;
  gap: ${size.MARGIN_1};
`;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 0;
  flex: 1;
  flex-flow: column nowrap;
  padding: ${size.MARGIN_1};
  gap: ${size.MARGIN_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_BOX_BORDER_COLOR};
  border-radius: ${size.BORDER_RADIUS_2};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const SectionContainer = styled.div`
  display: flex;
  width: 100%;
  flex-shrink: 0;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
`;

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  flex-shrink: 0;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
`;

const DeleteContainer = styled.div`
  display: flex;
  width: 100%;
  flex-shrink: 0;
  padding-top: ${size.MARGIN_1};
  border-top: ${size.BORDER_WIDTH_1} solid ${color.GRAY_2};
`;

const SectionHeader = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: ${size.MARGIN_1};
`;

const SectionTitleContainer = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 5px;
`;

const SectionHeaderActionContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 5px;
`;

const MemoSummaryContainer = styled.div`
  display: flex;
  width: 100%;
  padding: ${size.MARGIN_1};
  align-items: center;
  gap: ${size.MARGIN_1};
  background-color: ${color.CYAN_5};
  border: ${size.BORDER_WIDTH_1} solid ${color.CYAN_4};
  border-radius: ${size.BORDER_RADIUS_2};
`;

const MemoThumbnail = styled.div`
  display: flex;
  width: 96px;
  flex: 0 0 96px;
  padding: 5px;
  align-items: center;
  background-color: ${color.WHITE_1};
  border-radius: ${size.BORDER_RADIUS_1};
`;

const MemoSummaryActionContainer = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 5px;
`;

const HEADER_TITLE_DESIGN = {
  text: { size: 'medium' as const, color: color.CYAN_2, bold: true, oneline: true },
};

const PropertyItemsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-flow: column nowrap;
  padding: 5px ${size.MARGIN_1};
  background-color: ${color.CYAN_5};
  border: ${size.BORDER_WIDTH_1} solid ${color.CYAN_4};
  border-radius: ${size.BORDER_RADIUS_2};
`;

const PropertyItemContainer = styled.div`
  display: flex;
  width: 100%;
  height: 40px;
  align-items: center;
  gap: 5px;
`;

const PropertyView = styled(Handwriting)`
  display: block;
  min-width: 0;
  height: 100%;
  flex: 1;
`;

const PROPERTY_LABEL_DESIGN = {
  box: { width: '80px', alignX: 'left' as const },
  text: { size: 'small' as const, color: color.CYAN_2, bold: true, oneline: true },
};

const formatDate = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
};

const AnnotationSideControl: React.FC<IProps> = ({
  issueName,
  memo,
  photoCount,
  photoMessage,
  photos = [],
  photoBusy = false,
  photoRetryable = false,
  handlePhotoRetryClick,
  handlePhotoCancelClick,
  handlePhotoDeleteClick,
  handlePhotoOpenClick,
  property,
  handlePropertyClick,
  handleMemoAddClick,
  handleMemoListClick,
  handleMemoClick,
  handlePhotoClick,
  handleDeleteClick,
}) => {
  const latestMemo = memo[memo.length - 1];
  const latestMemoDate = formatDate(latestMemo?.updatedAt);

  const PhotoItems = photos.map((photo) => (
    <PhotoItemContainer key={photo.key}>
      <ImageBlock
        src={photo.imageLink || ''}
        alt={photo.filename}
        design={{ box: { width: '100%', event: { cursor: 'pointer' } }, image: { width: '100%', height: '90px' } }}
        click={{
          handler: () => {
            if (photo.imageLink) handlePhotoOpenClick?.(photo.imageLink);
          },
        }}
      />
      <IconTextBlock
        icon='trash'
        text='삭제'
        design={{
          box: { gap: '5px', event: { cursor: 'pointer' } },
          text: { size: 'tiny', color: color.GRAY_1 },
          icon: { size: 'tiny', color: color.GRAY_1 },
        }}
        click={{
          handler: () => {
            if (!photoBusy) handlePhotoDeleteClick?.(photo.key);
          },
        }}
      />
    </PhotoItemContainer>
  ));

  const PropertyItems = property?.map((item) => (
    <PropertyItemContainer key={uuid4()}>
      <TextBlock text={`${item.type === 'width' ? '폭' : '길이'}(${item.unit})`} design={PROPERTY_LABEL_DESIGN} />
      {item.value !== undefined ? (
        <TextBlock text={`${item.value}`} design={{ text: { size: 'medium', color: color.CYAN_2, bold: true } }} />
      ) : (
        <PropertyView type='read' value={item} theme={ETheme.CYAN} preserveAspectRatio='xMinYMid meet' />
      )}
    </PropertyItemContainer>
  ));

  return (
    <Wrapper>
      <SectionHeader>
        <TextBlock text={issueName} design={HEADER_TITLE_DESIGN} />
        <IconTextBlock
          icon='edit'
          text='수정하기'
          design={{
            text: { size: 'tiny', color: color.GRAY_1, underline: true, oneline: true },
            icon: { size: 'tiny', color: color.GRAY_1, kind: 'outlined' },
            box: { gap: '5px', event: { cursor: 'pointer' } },
          }}
          click={{ handler: handlePropertyClick }}
        />
      </SectionHeader>
      <ScrollContainer width='100%'>
        <Scroll direction='y' height='100%'>
          <ContentContainer>
            <SectionContainer>
              <PropertyItemsContainer>
                {property?.length ? (
                  PropertyItems
                ) : (
                  <TextBlock
                    text='물성치 입력'
                    design={{ text: { size: 'medium', color: color.CYAN_2, bold: true } }}
                  />
                )}
              </PropertyItemsContainer>
            </SectionContainer>
            <Line direction='horizontal' width='100%' color={color.GRAY_2} />
            <SectionContainer>
              <SectionHeader>
                <SectionTitleContainer>
                  <TextBlock text='메모' design={HEADER_TITLE_DESIGN} />
                  <Chip
                    text={`${memo.length}건`}
                    width='34px'
                    size='tiny'
                    usage='static'
                    kind='filled'
                    theme={ETheme.CYAN}
                  />
                </SectionTitleContainer>
                <SectionHeaderActionContainer>
                  <IconTextBlock
                    icon='add'
                    text='새 메모'
                    design={{
                      box: { gap: '5px', event: { cursor: 'pointer' } },
                      text: { size: 'small', color: color.CYAN_2, oneline: true },
                      icon: { size: 'small', color: color.CYAN_2, kind: 'outlined' },
                    }}
                    click={{ handler: handleMemoAddClick }}
                  />
                </SectionHeaderActionContainer>
              </SectionHeader>
              {latestMemo ? (
                <>
                  <MemoSummaryContainer>
                    <MemoThumbnail>
                      <Handwriting type='read' value={latestMemo} theme={ETheme.CYAN} />
                    </MemoThumbnail>
                    <MemoSummaryActionContainer>
                      <TextBlock
                        text='최근 메모'
                        design={{ text: { size: 'small', color: color.CYAN_2, bold: true, oneline: true } }}
                      />
                      {latestMemoDate && (
                        <TextBlock
                          text={latestMemoDate}
                          design={{ text: { size: 'tiny', color: color.GRAY_1, oneline: true } }}
                        />
                      )}
                      <IconTextBlock
                        icon='edit'
                        text='수정하기'
                        design={{
                          text: { size: 'tiny', color: color.GRAY_1, underline: true, oneline: true },
                          icon: { size: 'tiny', color: color.GRAY_1, kind: 'outlined' },
                          box: { gap: '5px', event: { cursor: 'pointer' } },
                        }}
                        click={{ handler: () => handleMemoClick?.(latestMemo) }}
                      />
                    </MemoSummaryActionContainer>
                  </MemoSummaryContainer>
                  <TextButton
                    text='메모 더 보기'
                    theme={ETheme.CYAN}
                    kind='outlined'
                    size='small'
                    shape='square'
                    width='100%'
                    handleClick={handleMemoListClick}
                  />
                </>
              ) : (
                <TextBlock
                  text='등록된 메모가 없습니다.'
                  design={{
                    box: { width: '100%', alignX: 'left' },
                    text: { size: 'small', color: color.GRAY_1 },
                  }}
                />
              )}
            </SectionContainer>
            <Line direction='horizontal' width='100%' color={color.GRAY_2} />
            <SectionContainer>
              <SectionHeader>
                <SectionTitleContainer>
                  <TextBlock text='사진' design={HEADER_TITLE_DESIGN} />
                  <Chip
                    text={`${photoCount}장`}
                    width='34px'
                    size='tiny'
                    usage='static'
                    kind='filled'
                    theme={ETheme.CYAN}
                  />
                </SectionTitleContainer>
                <SectionHeaderActionContainer>
                  <IconTextBlock
                    icon='add'
                    text='새 사진'
                    design={{
                      box: { gap: '5px', event: { cursor: 'pointer' } },
                      text: {
                        size: 'small',
                        color: photoBusy || photoRetryable ? color.GRAY_1 : color.CYAN_2,
                        oneline: true,
                      },
                      icon: {
                        size: 'small',
                        color: photoBusy || photoRetryable ? color.GRAY_1 : color.CYAN_2,
                        kind: 'outlined',
                      },
                    }}
                    click={{ handler: photoBusy || photoRetryable ? undefined : handlePhotoClick }}
                  />
                </SectionHeaderActionContainer>
              </SectionHeader>
              {PhotoItems.length > 0 && <PhotoItemsContainer>{PhotoItems}</PhotoItemsContainer>}
              {(photoMessage || photoCount === 0) && (
                <TextBlock
                  text={photoMessage || '등록된 사진이 없습니다.'}
                  design={{ box: { width: '100%', alignX: 'left' }, text: { size: 'small', color: color.GRAY_1 } }}
                />
              )}
              {photoRetryable && (
                <PhotoControlContainer>
                  <TextButton
                    text='다시 시도'
                    theme={ETheme.CYAN}
                    size='small'
                    disabled={photoBusy}
                    handleClick={handlePhotoRetryClick}
                  />
                  <TextButton
                    text='취소'
                    theme={ETheme.GRAY}
                    size='small'
                    disabled={photoBusy}
                    handleClick={handlePhotoCancelClick}
                  />
                </PhotoControlContainer>
              )}
            </SectionContainer>
          </ContentContainer>
        </Scroll>
      </ScrollContainer>
      <DeleteContainer>
        <TextButton
          icon='trash'
          text='기록 삭제'
          arrangement='icon-text'
          theme={ETheme.RED}
          kind='filled'
          size='medium'
          shape='pill'
          width='100%'
          handleClick={handleDeleteClick}
        />
      </DeleteContainer>
    </Wrapper>
  );
};

export default AnnotationSideControl;
